// apolo/src/lib/context/ruleActivation.js — harness-uplift-r2 E3.
// @arch:pure-engine
//
// Cascade-style graduated rule/skill activation. Today Apolo loads skills
// binary (on/off); this resolves FOUR modes that control how much of a rule
// enters context, and at what cost:
//   always_on      → full content, every message
//   model_decision → only the description in-prompt; body loaded on demand
//   glob           → full content when a touched file matches the glob(s)
//   manual          → only when explicitly @mentioned by name
//
// Reuses matchesGlob from steeringScope (no new glob dependency). Pure; never
// throws. Hard caps mirror Cascade: global 6k / workspace 12k chars.

import { matchesGlob } from '../globMatch.js';

export const GLOBAL_CAP = 6000;
export const WORKSPACE_CAP = 12000;

function capFor(scope) {
  return scope === 'global' ? GLOBAL_CAP : WORKSPACE_CAP;
}

function capContent(content, scope) {
  const s = typeof content === 'string' ? content : '';
  const cap = capFor(scope);
  return s.length > cap ? s.slice(0, cap) : s;
}

// Typed factories keep `include` a string LITERAL (not widened to string) so the
// JSDoc union return type of resolveActivation checks cleanly.
const NONE = (reason) => ({ include: /** @type {'none'} */ ('none'), reason, content: '' });
const FULL = (reason, content) => ({ include: /** @type {'full'} */ ('full'), reason, content });
const DESC = (content) => ({ include: /** @type {'description'} */ ('description'), reason: 'model_decision', content });

/** glob mode: full when a touched path matches any glob, else none. */
function activateGlob(rule, c, fullContent) {
  const globs = Array.isArray(rule.globs) ? rule.globs : [];
  const paths = Array.isArray(c.paths) ? c.paths : [];
  const hit = paths.some((p) => globs.some((g) => matchesGlob(p, g)));
  return hit ? FULL('glob-match', fullContent) : NONE('glob-no-match');
}

/** manual mode: full only when explicitly @mentioned by name, else none. */
function activateManual(rule, c, fullContent) {
  const mentioned = Array.isArray(c.mentioned) ? c.mentioned : [];
  const named = typeof rule.name === 'string' && mentioned.includes(rule.name);
  return named ? FULL('manual-mention', fullContent) : NONE('manual-idle');
}

function activate(rule, ctx) {
  if (!rule || typeof rule !== 'object') return NONE('no-rule');
  const scope = rule.scope === 'global' ? 'global' : 'workspace';
  const fullContent = capContent(rule.content, scope);
  const c = ctx && typeof ctx === 'object' ? ctx : {};

  if (rule.trigger === 'always_on') return FULL('always_on', fullContent);
  if (rule.trigger === 'model_decision') return DESC(typeof rule.description === 'string' ? rule.description : '');
  if (rule.trigger === 'glob') return activateGlob(rule, c, fullContent);
  return activateManual(rule, c, fullContent); // 'manual' + unknown/missing → safest (none unless mentioned)
}

/**
 * Resolve how a rule enters context for this turn.
 *
 * @param {{ trigger?: string, name?: string, description?: string, content?: string,
 *           globs?: string[], scope?: 'global'|'workspace' }} rule
 * @param {{ paths?: string[], mentioned?: string[] }} ctx - touched file paths + @mentions
 * @returns {{ include: 'full'|'description'|'none', reason: string, content: string }}
 */
export function resolveActivation(rule, ctx) {
  try {
    return activate(rule, ctx);
  } catch {
    return NONE('error');
  }
}
